import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, TrendingUp, DollarSign } from 'lucide-react';
import { receiptApi, analyticsApi } from '../lib/api';
import { formatCurrency, formatDate } from '../utils/format';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

export default function QuickReports() {
  const [reportType, setReportType] = useState<string>('');
  const [selectedVendor, setSelectedVendor] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [dateRange, setDateRange] = useState<'this-month' | 'last-month' | 'custom'>('last-month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Calculate date ranges
  const getDateRange = () => {
    const now = new Date();
    switch (dateRange) {
      case 'this-month':
        return {
          from: format(startOfMonth(now), 'yyyy-MM-dd'),
          to: format(endOfMonth(now), 'yyyy-MM-dd'),
        };
      case 'last-month':
        const lastMonth = subMonths(now, 1);
        return {
          from: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
          to: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
        };
      case 'custom':
        return {
          from: customFrom,
          to: customTo,
        };
      default:
        return { from: '', to: '' };
    }
  };

  const { from, to } = getDateRange();

  // Vendor spending query
  const { data: vendorData, isLoading: vendorLoading } = useQuery({
    queryKey: ['vendor-spending', selectedVendor, from, to],
    queryFn: async () => {
      const response = await receiptApi.list({ from, to });
      const receipts = response.data.receipts || [];
      
      const vendorReceipts = selectedVendor
        ? receipts.filter((r: any) => r.vendor_name === selectedVendor)
        : receipts;
      
      const total = vendorReceipts.reduce((sum: number, r: any) => sum + (r.total_amount || 0), 0);
      const count = vendorReceipts.length;
      const avgAmount = count > 0 ? total / count : 0;
      
      return {
        vendor: selectedVendor || 'All Vendors',
        total,
        count,
        avgAmount,
        receipts: vendorReceipts,
      };
    },
    enabled: reportType === 'vendor' && !!from && !!to,
  });

  // Category spending query - not used in current UI but ready for expansion
  useQuery({
    queryKey: ['category-spending', selectedCategory, from, to],
    queryFn: () => analyticsApi.getSpending({
      from,
      to,
      groupBy: 'category',
    }),
    enabled: reportType === 'category' && !!from && !!to && !!selectedCategory,
  });

  const handleRunReport = () => {
    // Trigger the query by setting report type
    if (selectedVendor) {
      setReportType('vendor');
    } else if (selectedCategory) {
      setReportType('category');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Quick Reports</h1>
        <p className="text-gray-600">
          Run common reports with just a few clicks
        </p>
      </div>

      {/* Quick Report Templates */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <button
          onClick={() => {
            setReportType('vendor');
            setSelectedVendor('');
            setDateRange('last-month');
          }}
          className="card text-left hover:shadow-md transition-shadow"
        >
          <DollarSign className="h-8 w-8 text-green-600 mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">
            Spending by Vendor
          </h3>
          <p className="text-sm text-gray-600">
            How much did we spend at a specific vendor?
          </p>
        </button>

        <button
          onClick={() => {
            setReportType('category');
            setDateRange('last-month');
          }}
          className="card text-left hover:shadow-md transition-shadow"
        >
          <TrendingUp className="h-8 w-8 text-blue-600 mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">
            Spending by Category
          </h3>
          <p className="text-sm text-gray-600">
            How much did we spend on fuel, food, parts, etc?
          </p>
        </button>

        <button
          onClick={() => {
            setReportType('monthly');
            setDateRange('last-month');
          }}
          className="card text-left hover:shadow-md transition-shadow"
        >
          <Calendar className="h-8 w-8 text-purple-600 mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">
            Monthly Summary
          </h3>
          <p className="text-sm text-gray-600">
            Complete spending breakdown for any month
          </p>
        </button>
      </div>

      {/* Report Configuration */}
      {reportType && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Configure Report
          </h2>

          <div className="space-y-4">
            {/* Date Range Selector */}
            <div>
              <label className="label">Date Range</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setDateRange('this-month')}
                  className={`btn ${
                    dateRange === 'this-month' ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  This Month
                </button>
                <button
                  onClick={() => setDateRange('last-month')}
                  className={`btn ${
                    dateRange === 'last-month' ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  Last Month
                </button>
                <button
                  onClick={() => setDateRange('custom')}
                  className={`btn ${
                    dateRange === 'custom' ? 'btn-primary' : 'btn-secondary'
                  }`}
                >
                  Custom Range
                </button>
              </div>
            </div>

            {dateRange === 'custom' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">From Date</label>
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">To Date</label>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="input"
                  />
                </div>
              </div>
            )}

            {/* Vendor Selector */}
            {reportType === 'vendor' && (
              <div>
                <label className="label">Vendor Name</label>
                <input
                  type="text"
                  placeholder="e.g., John Deere, Walmart, Casey's"
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave blank to see all vendors
                </p>
              </div>
            )}

            {/* Category Selector */}
            {reportType === 'category' && (
              <div>
                <label className="label">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="input"
                >
                  <option value="">Select a category</option>
                  <option value="Fuel & Gas">Fuel & Gas</option>
                  <option value="Parts & Supplies">Parts & Supplies</option>
                  <option value="Seed & Fertilizer">Seed & Fertilizer</option>
                  <option value="Office Supplies">Office Supplies</option>
                  <option value="Meals & Entertainment">Food/Meals</option>
                  <option value="Travel & Lodging">Travel & Lodging</option>
                  <option value="Maintenance & Repair">Maintenance & Repair</option>
                  <option value="Professional Services">Professional Services</option>
                </select>
              </div>
            )}

            <button onClick={handleRunReport} className="btn-primary">
              Run Report
            </button>
          </div>
        </div>
      )}

      {/* Vendor Report Results */}
      {reportType === 'vendor' && vendorData && !vendorLoading && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card">
              <p className="text-sm text-gray-600">Total Spent</p>
              <p className="text-3xl font-bold text-gray-900">
                {formatCurrency(vendorData.total)}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-600">Number of Receipts</p>
              <p className="text-3xl font-bold text-gray-900">
                {vendorData.count}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-600">Average per Receipt</p>
              <p className="text-3xl font-bold text-gray-900">
                {formatCurrency(vendorData.avgAmount)}
              </p>
            </div>
          </div>

          {/* Receipt List */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Receipt Details
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Payment
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vendorData.receipts.map((receipt: any) => (
                    <tr key={receipt.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(receipt.transaction_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {receipt.vendor_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {formatCurrency(receipt.total_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {receipt.payment_method || 'N/A'}
                        {receipt.check_number && ` - Check #${receipt.check_number}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Common Questions */}
      <div className="card bg-blue-50 border-blue-200">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          Common Questions You Can Answer
        </h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• "How much did I spend on food last month?" → Run Category Report for "Meals & Entertainment"</li>
          <li>• "How much did we spend at John Deere last month?" → Run Vendor Report for "John Deere"</li>
          <li>• "What did we spend on fuel in Q1?" → Category Report for "Fuel & Gas" with custom dates</li>
          <li>• "Show me all receipts over $500" → Go to Search, filter by amount range</li>
          <li>• "Find my truck insurance" → Go to Search, type "truck insurance" or VIN number</li>
          <li>• "Show me all check #1234 receipts" → Search for "check 1234"</li>
        </ul>
      </div>
    </div>
  );
}

